// src/pages/AdvancedRemixPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import './AdvancedRemixPage.css';

// Initial Data - Added seed for visualization
const INITIAL_TRACKS = [
  { id: 1, name: '人声', icon: 'fa-microphone-alt', color: '#667eea', bg: '#eff3ff', isMuted: false, seed: 100 },
  { id: 2, name: '吉他', icon: 'fa-guitar', color: '#f6b93b', bg: '#fffbf0', isMuted: false, seed: 200 },
  { id: 3, name: '808 鼓', icon: 'fa-drum', color: '#e55039', bg: '#fff0ee', isMuted: false, seed: 300 },
  { id: 4, name: '钢琴', icon: 'fa-keyboard', color: '#78e08f', bg: '#f0fff4', isMuted: false, seed: 400 },
];

const INSTRUMENT_LIBRARY = [
    { name: '三角钢琴', icon: 'fa-music', color: '#78e08f', bg: '#f0fff4' },
    { name: '电吉他', icon: 'fa-bolt', color: '#f6b93b', bg: '#fffbf0' },
    { name: '贝斯', icon: 'fa-guitar', color: '#6c5ce7', bg: '#f3f0ff' },
    { name: '架子鼓', icon: 'fa-drum', color: '#e55039', bg: '#fff0ee' },
    { name: '小提琴', icon: 'fa-italic', color: '#e84393', bg: '#ffeaf4' },
    { name: '萨克斯', icon: 'fa-wind', color: '#fdcb6e', bg: '#fff8e1' },
    { name: '合成器', icon: 'fa-wave-square', color: '#0984e3', bg: '#e1f5fe' },
    { name: '小号', icon: 'fa-bullhorn', color: '#d63031', bg: '#ffecec' },
    { name: '木吉他', icon: 'fa-tree', color: '#b71540', bg: '#fdecf0' },
    { name: '竖琴', icon: 'fa-bezier-curve', color: '#00b894', bg: '#e0fcf6' },
    { name: '马林巴', icon: 'fa-border-all', color: '#e17055', bg: '#ffebe6' },
    { name: '长笛', icon: 'fa-feather-alt', color: '#74b9ff', bg: '#eff7ff' },
];

// Reusable Button Component for Modal
const InstrumentOptionBtn = ({ icon, name, color, isSmart, onClick }) => (
    <div className="inst-option-btn" onClick={onClick}>
        <div className="inst-btn-icon" style={{ backgroundColor: color }}>
            <i className={`fas ${icon}`}></i>
        </div>
        <span className="inst-btn-label">{name}</span>
        {isSmart && <div className="smart-tag">智能推荐</div>}
    </div>
);

function AdvancedRemixPage({ onBack }) {
  const [tracks, setTracks] = useState(INITIAL_TRACKS);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadPercent, setPlayheadPercent] = useState(0); 
  const [isDragging, setIsDragging] = useState(false);
  const [excludedMeasures, setExcludedMeasures] = useState({});
  const [deleteMode, setDeleteMode] = useState(false);
  const [soloMode, setSoloMode] = useState(false);
  const [randomMode, setRandomMode] = useState(false); // 新增随机模式状态

  // Modal State
  const [modalType, setModalType] = useState(null); // null | 'add' | 'change'
  const [editingTrackId, setEditingTrackId] = useState(null);
  const [showFullLibrary, setShowFullLibrary] = useState(false);
  
  // Alert State
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  
  const playheadAreaRef = useRef(null);

  // --- Animation Logic ---
  useEffect(() => {
    let anim;
    if (isPlaying && !isDragging) {
      const startTime = Date.now();
      const duration = 5000;
      const startPercent = playheadPercent;

      const step = () => {
        const elapsed = Date.now() - startTime;
        const loopPercent = (startPercent + (elapsed / duration) * 100) % 100;
        setPlayheadPercent(loopPercent);
        anim = requestAnimationFrame(step);
      };
      
      anim = requestAnimationFrame(step);
    }
    return () => cancelAnimationFrame(anim);
  }, [isPlaying, isDragging]);

  // --- Track Interaction ---
  const toggleMeasure = (trackId, index) => {
    if (isDragging || deleteMode || soloMode || randomMode) return;
    const key = `${trackId}-${index}`;
    setExcludedMeasures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTrackIconClick = (trackId) => {
    if (deleteMode) {
        // Just delete, do NOT exit delete mode immediately
        setTracks(prev => prev.filter(t => t.id !== trackId));
    } else if (soloMode) {
        // Toggle Mute/Active in Solo Mode
        setTracks(prev => prev.map(t => 
            t.id === trackId ? { ...t, isMuted: !t.isMuted } : t
        ));
    } else if (randomMode) {
        // Random Melody Generation Mode
        // Update the seed to trigger re-render of notes
        setTracks(prev => prev.map(t =>
            t.id === trackId ? { ...t, seed: Math.random() * 10000 } : t
        ));
    } else {
        // Normal mode: Change instrument
        setEditingTrackId(trackId);
        setModalType('change');
    }
  };

  const handleAddTrackClick = () => {
      setModalType('add');
  };

  const handleApplyInstrument = (instrumentData) => {
      const newTrackData = { ...instrumentData, isMuted: false, seed: Math.random() * 500 };

      if (modalType === 'change' && editingTrackId) {
          setTracks(prev => prev.map(t => 
             t.id === editingTrackId ? { ...t, ...newTrackData } : t
          ));
      } else if (modalType === 'add') {
          const newId = Math.max(...tracks.map(t => t.id), 0) + 1;
          setTracks(prev => [...prev, { id: newId, ...newTrackData }]);
      }
      closeModals();
  };

  const closeModals = () => {
      setModalType(null);
      setEditingTrackId(null);
      setShowFullLibrary(false);
  };

  // --- Playhead Dragging ---
  const handleDragStart = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    updatePlayheadPosition(e);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    updatePlayheadPosition(e);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const updatePlayheadPosition = (e) => {
    if (!playheadAreaRef.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const rect = playheadAreaRef.current.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    setPlayheadPercent((x / rect.width) * 100);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchend', handleDragEnd);
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
    } else {
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchend', handleDragEnd);
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('touchmove', handleDragMove);
    }
    return () => {
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('touchmove', handleDragMove);
    };
  }, [isDragging]);

  // --- Helpers ---
  // Updated to use seed instead of trackId for visualization
  const renderVisualNotes = (seed, color) => {
    const notes = [];
    for (let i = 0; i < 16; i++) {
        const leftPct = (i / 16) * 100 + (Math.sin(seed + i) * 2);
        const widthPct = 4 + Math.abs(Math.cos(seed * i) * 3);
        const topPct = 10 + Math.abs(Math.sin(seed + i * 5) * 70);
        notes.push(
            <div 
                key={i} 
                className="fl-note"
                style={{
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    top: `${topPct}%`,
                    backgroundColor: color
                }}
            />
        );
    }
    return notes;
  };

  const quickOptions = [
    INSTRUMENT_LIBRARY[6], // 合成器
    INSTRUMENT_LIBRARY[11], // 长笛
    INSTRUMENT_LIBRARY[0]   // 三角钢琴
  ];

  return (
    <div 
        className="advanced-remix-page"
        onTouchStart={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
    >
      <div className="advanced-header">
        {/* 左上角触发退出确认 */}
        <button className="icon-btn" onClick={() => setShowExitConfirm(true)}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <h2>分解魔法</h2>
        {/* 垃圾桶按钮：点击开启/关闭删除模式 */}
        <button 
            className={`icon-btn ${deleteMode ? 'active-state' : ''}`}
            onClick={() => {
                setDeleteMode(!deleteMode);
                setSoloMode(false); 
                setRandomMode(false);
            }}
        >
          <i className={`fas ${deleteMode ? 'fa-trash-alt' : 'fa-trash'}`}></i>
        </button>
      </div>

      <div className="tracks-viewport">
        <div 
            className="timeline-ruler-area"
            onTouchStart={handleDragStart}
            onMouseDown={handleDragStart}
        ></div>

        <div className="tracks-content">
            <div className="playhead-overlay-container" ref={playheadAreaRef}>
                <div 
                    className={`global-playhead ${isDragging ? 'dragging' : ''}`} 
                    style={{ left: `${playheadPercent}%` }}
                >
                    <div 
                        className="playhead-handle"
                        onTouchStart={handleDragStart}
                        onMouseDown={handleDragStart}
                    >
                        <i className="fas fa-grip-lines-vertical"></i>
                    </div>
                </div>
            </div>

            {tracks.map((track) => (
                <div 
                    key={track.id} 
                    className={`track-container ${deleteMode ? 'shake-anim' : ''} ${track.isMuted ? 'is-muted' : ''}`}
                    style={{ backgroundColor: track.bg }}
                >
                    <div 
                        className="track-header" 
                        onClick={() => handleTrackIconClick(track.id)}
                    >
                        <div className="track-icon" style={{ backgroundColor: track.color }}>
                            {deleteMode ? (
                                <i className="fas fa-minus"></i>
                            ) : (
                                <>
                                    <i className={`fas ${track.icon}`}></i>
                                    {/* 显示红色 X 号如果在非独奏模式下被静音 */}
                                    {track.isMuted && !soloMode && (
                                        <div className="muted-x-overlay">
                                            <i className="fas fa-times"></i>
                                        </div>
                                    )}
                                    {/* 随机模式下的遮罩 */}
                                    {randomMode && (
                                        <div className="random-overlay">
                                            <i className="fas fa-dice"></i>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <span className="track-name">{track.name}</span>
                    </div>

                    <div className="track-piano-roll">
                        {/* 传入 seed 而不是 id */}
                        {renderVisualNotes(track.seed || track.id, track.color)}
                        <div className="measures-grid-overlay">
                            {[0, 1, 2, 3].map((index) => {
                                const isExcluded = excludedMeasures[`${track.id}-${index}`];
                                return (
                                    <div 
                                        key={index}
                                        className={`measure-cell ${isExcluded ? 'excluded' : ''}`}
                                        onClick={() => toggleMeasure(track.id, index)}
                                    >
                                        <i className="fas fa-times measure-x-icon"></i>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ))}

            <div className="track-container add-track-container" onClick={handleAddTrackClick}>
                <div className="add-track-content">
                    <i className="fas fa-plus-circle"></i>
                    <span>添加新乐器</span>
                </div>
            </div>
        </div>
      </div>

      <div className="advanced-footer">
        {/* 左侧组：独奏 + 随机按钮 */}
        <div className="footer-left-group">
            <button 
                className={`icon-btn ${soloMode ? 'active-solo' : ''}`}
                onClick={() => {
                    setSoloMode(!soloMode);
                    setDeleteMode(false); 
                    setRandomMode(false);
                }}
            >
                <i className="fas fa-headphones-alt"></i>
            </button>

            {/* 新增：随机旋律按钮 */}
            <button 
                className={`icon-btn ${randomMode ? 'active-random' : ''}`}
                onClick={() => {
                    setRandomMode(!randomMode);
                    setSoloMode(false);
                    setDeleteMode(false);
                }}
            >
                <i className="fas fa-dice"></i>
            </button>
        </div>
        
        <button 
            className={`adv-play-btn ${isPlaying ? 'playing' : ''}`} 
            onClick={() => setIsPlaying(!isPlaying)}
        >
            <i className={`fas fa-${isPlaying ? 'stop' : 'play'}`}></i>
        </button>

        {/* 右下角完成按钮 */}
        <button 
            className={`icon-btn ${(deleteMode || soloMode || randomMode) ? 'disabled' : ''}`} 
            onClick={(deleteMode || soloMode || randomMode) ? null : onBack}
        >
             <i className="fas fa-check"></i>
        </button>
      </div>

      {/* --- Exit Confirmation Modal --- */}
      {showExitConfirm && (
        <div className="alert-overlay">
            <div className="alert-box">
                <h3>确认退出？</h3>
                <p>您尚未保存当前的编曲进度，退出后所有修改将丢失。</p>
                <div className="alert-buttons">
                    <button className="alert-btn cancel" onClick={() => setShowExitConfirm(false)}>
                        取消
                    </button>
                    <button className="alert-btn confirm" onClick={onBack}>
                        不保存退出
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* --- Quick Select Modal --- */}
      {modalType && !showFullLibrary && !showExitConfirm && (
        <div className="instrument-modal-overlay" onClick={closeModals}>
            <div className="instrument-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header-bar"></div>
                <h3 className="modal-title">
                    {modalType === 'add' ? '添加乐器' : '更换乐器'}
                </h3>
                
                <div className="modal-options-grid">
                    <InstrumentOptionBtn 
                        {...quickOptions[0]} 
                        onClick={() => handleApplyInstrument(quickOptions[0])}
                    />
                    <InstrumentOptionBtn 
                        {...quickOptions[1]} 
                        onClick={() => handleApplyInstrument(quickOptions[1])}
                    />
                    <InstrumentOptionBtn 
                        {...quickOptions[2]} 
                        isSmart={true}
                        onClick={() => handleApplyInstrument(quickOptions[2])}
                    />
                    
                    {/* More Button */}
                    <div className="inst-option-btn more-btn" onClick={() => setShowFullLibrary(true)}>
                        <div className="inst-btn-icon" style={{ backgroundColor: '#f0f0f0', color: '#666' }}>
                            <i className="fas fa-ellipsis-h"></i>
                        </div>
                        <span className="inst-btn-label">更多乐器</span>
                    </div>
                </div>

                <button 
                    className="modal-close-btn"
                    onClick={closeModals}
                >
                    取消
                </button>
            </div>
        </div>
      )}

      {/* --- Full Library Page --- */}
      {showFullLibrary && (
          <div className="full-library-page">
              <div className="library-header">
                  <button className="icon-btn" onClick={() => setShowFullLibrary(false)}>
                      <i className="fas fa-chevron-left"></i>
                  </button>
                  <h3>所有乐器</h3>
                  <div style={{width: 40}}></div>
              </div>
              <div className="library-grid">
                  {INSTRUMENT_LIBRARY.map((inst, idx) => (
                      <div 
                        key={idx} 
                        className="library-item"
                        onClick={() => handleApplyInstrument(inst)}
                      >
                          <div className="library-icon" style={{backgroundColor: inst.color}}>
                              <i className={`fas ${inst.icon}`}></i>
                          </div>
                          <span>{inst.name}</span>
                      </div>
                  ))}
              </div>
          </div>
      )}

    </div>
  );
}

export default AdvancedRemixPage;